import { create } from 'zustand';
import { buildWsUrl } from '@/lib/api';

/**
 * Real-time per-device transport state for the dual-transport routing
 * model (USB primary + Wi-Fi standby).
 *
 * Subscribes to a single hub-wide WebSocket
 * (`/api/devices/transport/events`). First frame is a snapshot —
 * every visible record's current `{ kind, activeTransport, transports,
 * hwId, lastSeenAt }` — and each subsequent frame is a
 * `transport-change` delta for one record.
 *
 * Consumers:
 *   - `useTransport((s) => s.bySerial[serial])` — topbar transport badge
 *   - `useTransport.getState().recentFailovers` — failover toast ring buffer
 *
 * Lifecycle: `connect()` from a top-level effect; the store handles
 * exponential-ish reconnect (3 s cap) on close. `disconnect()` for
 * sign-out / tests.
 *
 * Note: `activeTransport.kind` mirrors `DeviceRecord['activeTransport']`
 * on the server — `'usb' | 'wireless' | 'agent' | 'fake' | undefined`.
 * The store accepts an explicit `undefined` to mean offline; missing
 * key means "haven't seen this device yet" (different UX).
 */

export type TransportKind = 'usb' | 'wireless' | 'tcpip' | 'agent' | 'fake';

export interface TransportSnapshot {
  kind: TransportKind;
  serial: string;
}

export interface TransportEntry {
  serial: string;
  kind: TransportKind;
  activeTransport?: TransportSnapshot;
  transports: Array<{ kind: TransportKind; serial: string; lastSeenAt: number }>;
  hwId?: string;
  lastSeenAt: number;
  /**
   * Runtime flag pushed by the server when USB is online but no
   * wireless-debug backup is provisioned for this physical device.
   * Consumed by `WirelessPairBanner` to surface a "[一键引导]" nudge.
   * Updated by `mirror-readiness` frames (independent of activeTransport
   * flips, so the banner reacts the moment provisioning fails without
   * waiting for a transport-change).
   */
  needsWirelessPair?: boolean;
  /**
   * Hint that this record has a tcpip-mirror transport registered
   * (whether or not it's currently in `adb devices`). The HTTP
   * /devices endpoint computes the authoritative `mirrorOnly` flag
   * from the live adb snapshot; the transport store uses this hint to
   * render the badge as "TCP-IP 镜像备线" while waiting for the next
   * HTTP refetch.
   */
  hasTcpipMirror?: boolean;
}

export interface FailoverToast {
  id: number;
  serial: string;
  previous?: TransportSnapshot;
  next?: TransportSnapshot;
  pushedAt: number;
}

const RECENT_TOAST_CAP = 6;

export interface ThroughputState {
  /** Bytes per second over the last sample window (≈1 s). */
  bytesPerSec: number;
  /** Wall-clock the sample landed; used to fade the meter to 0 when silent. */
  ts: number;
}

interface TransportState {
  bySerial: Record<string, TransportEntry>;
  /** Per-device bytes/sec; latest sample wins. */
  throughputBySerial: Record<string, ThroughputState>;
  recentFailovers: FailoverToast[];
  /**
   * Monotonic counter bumped on every `transport-change` frame, used by
   * `App.tsx` as a `useEffect` dep so it can invalidate the cached
   * `['devices']` react-query whenever the server decides USB ↔ Wi-Fi
   * failed over. Without this the device grid (5 s poll) shows offline
   * for up to 5 s after a USB unplug even though the wireless leg is
   * already serving the device.
   */
  transportChangeSeq: number;
  ws: WebSocket | null;
  connected: boolean;

  connect: () => void;
  disconnect: () => void;
  dismissFailover: (id: number) => void;
}

let toastSeq = 0;

/**
 * Compute a human-readable label + colour token for a transport entry.
 * Pure helper so the topbar badge / toast share a single source of
 * truth for "USB" / "Wi-Fi" / "离线" / "等待中" rendering.
 */
/** Format a bytes-per-second number into a short, eye-friendly unit. */
export function formatBytesPerSec(bps: number): string {
  if (!Number.isFinite(bps) || bps <= 0) return '0 B/s';
  if (bps < 1024) return `${bps} B/s`;
  if (bps < 1024 * 1024) return `${(bps / 1024).toFixed(1)} KB/s`;
  return `${(bps / (1024 * 1024)).toFixed(2)} MB/s`;
}

export function describeTransport(entry: TransportEntry | undefined): {
  label: string;
  short: string;
  /**
   * Render-tone bucket. Three real states + `unknown` (still loading)
   * after the tiered-transport refactor:
   *
   *   - `usb`        — primary ADB cable (green)
   *   - `wireless`   — Android 11+ wireless debugging (sky / blue)
   *   - `tcpip`      — `adb tcpip 5555` mirror-only backup (amber);
   *                    full adb is offline, only scrcpy works
   *   - `offline`    — no transport at all (rose / red)
   *   - `unknown`    — record not yet in store (grey)
   */
  tone: 'usb' | 'wireless' | 'tcpip' | 'offline' | 'unknown';
} {
  if (!entry) return { label: '等待中', short: '—', tone: 'unknown' };
  const at = entry.activeTransport;
  if (at) {
    if (at.kind === 'usb') return { label: 'USB', short: 'USB', tone: 'usb' };
    if (at.kind === 'wireless') {
      return { label: 'Wi-Fi（无线调试）', short: 'Wi-Fi', tone: 'wireless' };
    }
    if (at.kind === 'agent') return { label: 'Agent', short: 'Agent', tone: 'wireless' };
    if (at.kind === 'tcpip') {
      // recomputeActiveTransport currently never picks tcpip — keep
      // the branch in case the typing widens in future.
      return { label: 'TCP-IP 镜像备线', short: 'TCP-IP', tone: 'tcpip' };
    }
    return { label: at.kind, short: at.kind, tone: 'unknown' };
  }
  // No active adb transport. If we have a tcpip mirror registered the
  // SPA can still open scrcpy via the mirror-only fallback — surface
  // that distinct state instead of a plain "offline".
  if (entry.hasTcpipMirror) {
    return { label: 'TCP-IP 镜像备线 · 仅镜像', short: 'TCP-IP', tone: 'tcpip' };
  }
  return { label: '离线', short: '×', tone: 'offline' };
}

export const useTransport = create<TransportState>((set, get) => {
  let reconnectTimer: number | null = null;

  const open = (): void => {
    if (get().ws) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(buildWsUrl('/api/devices/transport/events'));
    } catch {
      scheduleReconnect();
      return;
    }
    set({ ws, connected: false });
    ws.onopen = (): void => set({ connected: true });
    ws.onmessage = (msg) => {
      let frame: {
        type: string;
        entries?: TransportEntry[];
        serial?: string;
        next?: TransportSnapshot;
        previous?: TransportSnapshot;
        bytes?: number;
        windowMs?: number;
        ts?: number;
        needsWirelessPair?: boolean;
      };
      try {
        frame = JSON.parse(typeof msg.data === 'string' ? msg.data : '') as typeof frame;
      } catch {
        return;
      }
      switch (frame.type) {
        case 'snapshot': {
          const byKey: Record<string, TransportEntry> = {};
          for (const e of frame.entries ?? []) byKey[e.serial] = e;
          // Also index by every transport alias so the topbar can look
          // a device up by either the primary serial OR the URL serial
          // (which may be the USB alias after a wireless attach).
          for (const e of frame.entries ?? []) {
            for (const t of e.transports ?? []) {
              if (t.serial !== e.serial && !byKey[t.serial]) byKey[t.serial] = e;
            }
          }
          set({ bySerial: byKey });
          return;
        }
        case 'transport-change': {
          if (!frame.serial) return;
          const serial = frame.serial;
          set((cur) => {
            const prev = cur.bySerial[serial];
            if (!prev) return cur;
            const updated: TransportEntry = { ...prev, activeTransport: frame.next };
            const aliasedKeys = Object.keys(cur.bySerial).filter(
              (k) => cur.bySerial[k] === prev
            );
            const nextBy = { ...cur.bySerial };
            for (const k of aliasedKeys) nextBy[k] = updated;
            // Surface failovers as toasts — but only meaningful ones:
            //   - USB → wireless        ("自动切换到 Wi-Fi 备用线")
            //   - any → offline         ("设备离线")
            //   - offline → any         ("设备恢复联机")
            const wasOffline = !frame.previous;
            const nowOffline = !frame.next;
            const wasUsb = frame.previous?.kind === 'usb';
            const nowWireless = frame.next?.kind === 'wireless';
            const shouldToast =
              (wasUsb && nowWireless) ||
              (!wasOffline && nowOffline) ||
              (wasOffline && !nowOffline);
            let recentFailovers = cur.recentFailovers;
            if (shouldToast) {
              const id = ++toastSeq;
              recentFailovers = [
                ...cur.recentFailovers,
                {
                  id,
                  serial,
                  previous: frame.previous,
                  next: frame.next,
                  pushedAt: Date.now(),
                },
              ];
              if (recentFailovers.length > RECENT_TOAST_CAP) {
                recentFailovers.splice(0, recentFailovers.length - RECENT_TOAST_CAP);
              }
            }
            return {
              bySerial: nextBy,
              recentFailovers,
              transportChangeSeq: cur.transportChangeSeq + 1,
            };
          });
          return;
        }
        case 'mirror-readiness': {
          if (!frame.serial) return;
          const serial = frame.serial;
          const needsWirelessPair = !!frame.needsWirelessPair;
          set((cur) => {
            const prev = cur.bySerial[serial];
            if (!prev) return cur;
            if ((prev.needsWirelessPair ?? false) === needsWirelessPair) return cur;
            const updated: TransportEntry = { ...prev, needsWirelessPair };
            const aliasedKeys = Object.keys(cur.bySerial).filter(
              (k) => cur.bySerial[k] === prev
            );
            const nextBy = { ...cur.bySerial };
            for (const k of aliasedKeys) nextBy[k] = updated;
            return { bySerial: nextBy };
          });
          return;
        }
        case 'throughput-sample': {
          if (!frame.serial || frame.bytes === undefined || !frame.windowMs) return;
          const bps =
            frame.windowMs > 0
              ? Math.round((frame.bytes / frame.windowMs) * 1000)
              : 0;
          const sample: ThroughputState = { bytesPerSec: bps, ts: frame.ts ?? Date.now() };
          set((cur) => ({
            throughputBySerial: { ...cur.throughputBySerial, [frame.serial!]: sample },
          }));
          return;
        }
      }
    };
    ws.onclose = (): void => {
      set({ ws: null, connected: false });
      scheduleReconnect();
    };
    ws.onerror = (): void => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
  };

  const scheduleReconnect = (): void => {
    if (reconnectTimer !== null) return;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      if (get().ws === null) open();
    }, 3_000);
  };

  return {
    bySerial: {},
    throughputBySerial: {},
    recentFailovers: [],
    transportChangeSeq: 0,
    ws: null,
    connected: false,

    connect: () => open(),
    disconnect: () => {
      const ws = get().ws;
      if (ws) {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      }
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      set({ ws: null, connected: false });
    },
    dismissFailover: (id) =>
      set((cur) => ({ recentFailovers: cur.recentFailovers.filter((t) => t.id !== id) })),
  };
});
